import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbSchema } from "@/lib/schema";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Pal Stars — Palworld TCG Price Index & Wiki" },
      { name: "description", content: "Pal Stars is a fan-made Palworld wiki with a daily TCG Price Chart and Price Index, plus Pals, breeding data and guides." },
      { property: "og:title", content: "About Pal Stars — Palworld TCG Price Index & Wiki" },
      { property: "og:description", content: "Fan-made Palworld wiki with a daily Palworld TCG Price Chart and TCG Price Index for collectors, investors and players." },
      { property: "og:url", content: "https://palstars.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Pal Stars — Fan-Made Palworld Wiki" },
      { name: "twitter:description", content: "Fan-made Palworld wiki. Constantly updated. Contribute development, images, fan art, or blog writing." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/about" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["About Pal Stars", "/about"]]),
{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About Pal Stars",
        url: "https://palstars.com/about",
        description: "Fan-made Palworld wiki with a daily Palworld TCG Price Chart and TCG Price Index for collectors, investors and players.",
        mainEntity: {
          "@type": "Organization",
          name: "Pal Stars",
          url: "https://palstars.com",
          description: "Palworld fan wiki and database, publisher of the Palworld TCG Price Chart and Palworld TCG Price Index.",
        },
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
      }),
    }],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <img
        src="/eidrolon-avatar-144.webp"
        alt="Pal Stars site icon"
        width={72}
        height={72}
        loading="lazy"
        decoding="async"
        className="h-16 w-16 sm:h-18 sm:w-18 rounded-2xl object-cover shadow-lg"
      />
      <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">About</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">
          About <span className="gradient-text">Pal Stars</span>
        </h1>
        <nav aria-label="Market tools" className="flex shrink-0 items-center gap-2">
          <Link
            to="/tcg-price-index"
            className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
          >
            TCG Price Index
          </Link>
          <Link
            to="/prices"
            className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-card"
          >
            TCG Price Chart
          </Link>
        </nav>
      </div>

      <p className="aeo-answer mt-4 text-lg text-muted-foreground">
        Pal Stars is an <strong>unofficial, fan-made Palworld wiki and database</strong> that also runs a{" "}
        <Link to="/prices" className="text-primary hover:underline">Palworld TCG Price Chart</Link> and the{" "}
        <Link to="/tcg-price-index" className="text-primary hover:underline">Palworld TCG Price Index</Link> — daily card
        prices and a benchmark index for collectors, investors and players — built by fans, for fans.
        It is not affiliated with, endorsed by, or sponsored by Pocketpair Inc. or the developers of Palworld.
        All trademarks, logos, character designs and screenshots belong to their respective owners.
      </p>

      <section className="mt-10 space-y-3">
        <h2 className="text-2xl font-bold">Constantly updated</h2>
        <p className="aeo-answer text-muted-foreground">
          The site is a living project. New Pals, variants, breeding routes, TCG cards, mount speeds,
          tier lists and guides are added as Palworld and the Palworld Official Card Game evolve.
          If a stat is wrong, a link is broken, or new content dropped and isn't here yet — that's
          because we're a small crew of fans, and we'd love your help catching it.
        </p>
      </section>

      <section className="mt-10 space-y-3">
        <h2 className="text-2xl font-bold">Help build Pal Stars</h2>
        <p className="text-muted-foreground">
          We're actively looking for community help. If any of these sound like you, please reach out
          through the <Link to="/contact" className="text-primary hover:underline">contact form</Link>:
        </p>
        <ul className="mt-2 grid gap-3 sm:grid-cols-2">
          {[
            { t: "🐛 Bug hunters", d: "Spot a broken link, wrong stat, or layout glitch? Tell us where and we'll fix it." },
            { t: "💻 Web developers", d: "React / TanStack Start / Tailwind contributors welcome — features, perf, accessibility." },
            { t: "🎨 Artists & fan art", d: "Original Pal fan art, portraits, banners, icons — credited and linked back to you." },
            { t: "📸 Screenshots & images", d: "Clean in-game captures for Pals, maps, breeding pens, TCG cards." },
            { t: "✍️ Blog & guide writers", d: "Long-form guides, tier lists, patch breakdowns, breeding routes." },
            { t: "🧪 Data & TCG nerds", d: "Card scans, deck lists, meta reports from the official card game." },
          ].map((r) => (
            <li key={r.t} className="rounded-xl glass p-4">
              <div className="font-semibold text-foreground">{r.t}</div>
              <div className="mt-1 text-sm text-muted-foreground">{r.d}</div>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <Link
            to="/contact"
            className="inline-flex items-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            Reach out via the contact form →
          </Link>
        </div>
      </section>

      <section className="mt-10 space-y-3">
        <h2 className="text-2xl font-bold">Help the site grow</h2>
        <p className="aeo-answer text-muted-foreground">
          Pal Stars is free, ad-light, and community-first. The best thing you can do — even if you can't
          contribute directly — is spread the word:
        </p>
        <ul className="mt-2 list-disc space-y-2 pl-6 text-muted-foreground">
          <li><strong className="text-foreground">Bookmark the site</strong> — press <kbd className="rounded border px-1.5 py-0.5 text-xs">Ctrl/⌘ + D</kbd> to save it for quick access.</li>
          <li>
            <strong className="text-foreground">Share links with friends</strong> on Reddit,{" "}
            <a href="https://tiktok.com/@palworldtcg" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TikTok</a>,{" "}
            <a href="https://instagram.com/palworld.tcg" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instagram</a>,{" "}
            X / Twitter, Discord, YouTube — anywhere Palworld fans gather.
          </li>

          <li>Drop a Pal card link into your guild or clan chat.</li>
          <li>Embed a guide in your own blog or wiki (attribution appreciated).</li>
        </ul>
      </section>

      <section className="mt-10 space-y-3">
        <h2 className="text-2xl font-bold">Get in touch</h2>
        <p className="text-muted-foreground">
          Bugs, ideas, corrections, contributor applications — all go through the same place:
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-card">Contact form</Link>
          <Link to="/guides" className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-card">Browse guides</Link>
          <Link to="/pals" className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-card">All Pals</Link>
        </div>
      </section>
    </div>
  );
}
